<template>
  <footer class="foot">
    <div ref="plate" class="foot__plate">
      <!-- Hung from the plate's top edge and clipped, so the links slide out
           from behind the footer rather than moving it. -->
      <transition name="slide">
        <div v-show="open" id="foot-legal" class="foot__menu">
          <div class="foot__menu-inner">
            <router-link
              v-for="item in items"
              :key="item.to"
              :to="item.to"
              class="foot__link"
            >{{ item.label }}</router-link>
          </div>
        </div>
      </transition>

      <div class="foot__row">
        <span class="foot__copy">&copy; {{ year }} ami*té</span>
        <span class="foot__lozenge" aria-hidden="true">◆</span>
        <button
          ref="toggle"
          type="button"
          class="foot__trigger"
          :aria-expanded="open ? 'true' : 'false'"
          aria-controls="foot-legal"
          @click="open = !open"
        >
          Bestimmungen &amp; Richtlinien
        </button>
      </div>
    </div>
  </footer>
</template>

<script>
const ITEMS = [
  { to: '/impressum', label: 'Impressum' },
  { to: '/datenschutz', label: 'Datenschutz' },
  { to: '/agb', label: 'AGB' },
  { to: '/widerrufsrecht', label: 'Widerrufsrecht' },
  { to: '/versand', label: 'Versand' },
];

export default {
  name: 'SiteFooter',
  data() {
    return {
      open: false,
      items: ITEMS,
      // Stamped once on load: the footer would otherwise be the only thing on
      // the page that changes without anyone touching it.
      year: new Date().getFullYear(),
    };
  },
  watch: {
    $route() {
      this.open = false;
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    onKeydown(event) {
      if (event.key !== 'Escape' || !this.open) return;
      this.open = false;
      this.$refs.toggle.focus();
    },
    onDocumentClick(event) {
      if (this.open && !this.$refs.plate.contains(event.target)) this.open = false;
    },
  },
};
</script>

<style scoped>
.foot {
  /* margin-top: auto pins it to the bottom of the #app column on pages too
     short to fill the window. */
  margin-top: auto;
  display: flex;
  justify-content: center;
  padding: 0 1rem 2.25rem;
}

.foot__plate {
  position: relative;
  padding: 0.7rem 1.5rem;

  background: rgba(250, 246, 240, 0.94);
  border: 1px solid rgba(58, 43, 30, 0.34);
  border-radius: 2px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.6),
    0 12px 26px -14px rgba(50, 35, 20, 0.65);
}

.foot__plate::before {
  content: '';
  position: absolute;
  inset: 4px;
  border: 1px solid rgba(58, 43, 30, 0.26);
  border-radius: 1px;
  pointer-events: none;
}

.foot__copy,
.foot__trigger {
  color: #3a2b1e;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  white-space: nowrap;
}

.foot__lozenge {
  color: rgba(58, 43, 30, 0.5);
  font-size: 0.5rem;
}

.foot__row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
}

.foot__trigger {
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.25s ease;
}

.foot__trigger:hover,
.foot__trigger:focus-visible {
  color: #2e4057;
}

.foot__trigger:focus-visible {
  outline: 2px solid #2e4057;
  outline-offset: 5px;
  border-radius: 1px;
}

/* Out of flow and hung from the plate's top edge, so opening grows the box
   upward instead of pushing the footer — and the page — down. Same width as
   the plate: one box that got taller, not a card floating over one.
   The wrapper only clips and casts; the panel itself slides inside it. */
.foot__menu {
  position: absolute;
  left: 0;
  right: 0;
  /* Overlaps the plate's own top border by exactly one pixel. */
  bottom: calc(100% - 1px);
  overflow: hidden;
  /* Deliberately unstyled. A shadow here belongs to the wrapper, which is at
     full size from the first frame, so it drew the panel's outline on the
     carpet while the panel was still sliding into it. */
}

.foot__menu-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.7rem;
  padding: 1.1rem 1.5rem;

  background: rgba(250, 246, 240, 0.94);
  border: 1px solid rgba(58, 43, 30, 0.34);
  border-bottom: 0;
  border-radius: 2px 2px 0 0;
  transition: transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.foot__menu-inner::before {
  content: '';
  position: absolute;
  inset: 4px 4px 0;
  border: 1px solid rgba(58, 43, 30, 0.26);
  border-bottom: 0;
  border-radius: 1px 1px 0 0;
  pointer-events: none;
}

/* Clipped by the wrapper, so it reads as sliding out of the footer itself. */
.slide-enter-from .foot__menu-inner,
.slide-leave-to .foot__menu-inner {
  transform: translateY(100%);
}

.slide-leave-active .foot__menu-inner {
  transition-duration: 0.22s;
  transition-timing-function: cubic-bezier(0.55, 0.06, 0.68, 0.19);
}

.foot__link {
  position: relative;
  color: #3a2b1e;
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: color 0.25s ease;
}

.foot__link:hover,
.foot__link:focus-visible {
  color: #2e4057;
}

.foot__link:focus-visible {
  outline: 2px solid #2e4057;
  outline-offset: 4px;
  border-radius: 1px;
}

.foot__link.router-link-exact-active {
  color: #2e4057;
}

/* Below this the copyright and the trigger stop sharing a line. */
@media (max-width: 460px) {
  .foot__plate {
    padding: 0.85rem 1.25rem;
  }

  .foot__row {
    flex-direction: column;
    gap: 0.5rem;
  }

  .foot__copy,
  .foot__trigger {
    font-size: 0.72rem;
    letter-spacing: 0.09em;
  }
}

@media (prefers-reduced-motion: reduce) {
  .foot__trigger,
  .foot__link,
  .foot__menu-inner {
    transition: none;
  }
}
</style>
